/**
 * Context per condividere i conteggi pending in tutta l'applicazione
 * Usato da menu, dashboard e altre componenti per mostrare badge/notifiche
 *
 * Supporta aggiornamenti real-time via WebSocket quando vengono creati
 * nuovi elementi pending (turbine o clienti temporanei)
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { registerMessageHandler } from '../hooks/useSharedWebSocket';
import { useAuth } from '../components/auth/AuthProvider';
import { useSede } from './SedeContext';
import { playMagazzinoNotification } from '../utils/audioNotifications';

interface PendingCountsState {
  apparati: number;
  clienti: number;
  analisi: number;
  preventivi: number;
  magazzino: number;
  total: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

const PendingCountsContext = createContext<PendingCountsState | undefined>(undefined);

const REFRESH_INTERVAL = 30000; // 30 secondi - aggiornamento più reattivo

export function PendingCountsProvider({ children }: { children: ReactNode }) {
  // Ottieni stato auth e sede corrente
  const { isAuthenticated } = useAuth();
  const { sedeCorrente } = useSede();

  const [counts, setCounts] = useState({
    apparati: 0,
    clienti: 0,
    analisi: 0,
    preventivi: 0,
    magazzino: 0,
    total: 0,
    loading: true
  });

  const fetchCounts = useCallback(async () => {
    // Skip fetch se non autenticato o su pagine mobile
    const path = window.location.pathname;
    if (!isAuthenticated || path.startsWith('/mobile-officina') || path.startsWith('/mobile-operator')) {
      setCounts(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      // Per magazzino, passa la sede per ottenere solo le notifiche rilevanti
      const magazzinoUrl = sedeCorrente
        ? `/api/magazzino/trasferimenti/stats/pending-count?sede=${sedeCorrente}`
        : '/api/magazzino/trasferimenti/stats/pending-count';

      const [apparatiRes, clientiRes, analisiRes, preventiviRes, magazzinoRes] = await Promise.all([
        fetch('/api/admin/pending-turbines/stats/count'),
        fetch('/api/admin/pending-customers/stats/count'),
        fetch('/api/admin/pending-analisi/stats/count'),
        fetch('/api/admin/pending-preventivi/stats/count'),
        fetch(magazzinoUrl)
      ]);

      const apparatiData = await apparatiRes.json();
      const clientiData = await clientiRes.json();
      const analisiData = await analisiRes.json();
      const preventiviData = await preventiviRes.json();
      const magazzinoData = await magazzinoRes.json();

      const apparatiCount = apparatiData.success ? apparatiData.pendingCount : 0;
      const clientiCount = clientiData.success ? clientiData.pendingCount : 0;
      const analisiCount = analisiData.success ? analisiData.pendingCount : 0;
      const preventiviCount = preventiviData.success ? preventiviData.pendingCount : 0;
      // Usa sedeActionCount se disponibile (notifiche per la sede), altrimenti pendingCount globale
      const magazzinoCount = magazzinoData.success
        ? (magazzinoData.sedeActionCount ?? magazzinoData.pendingCount)
        : 0;

      setCounts({
        apparati: apparatiCount,
        clienti: clientiCount,
        analisi: analisiCount,
        preventivi: preventiviCount,
        magazzino: magazzinoCount,
        total: apparatiCount + clientiCount + analisiCount + preventiviCount + magazzinoCount,
        loading: false
      });
    } catch (err) {
      // le fetch abortite da navigazione/smontaggio non sono errori (audit e2e 2026-07-15)
      if (!String((err as Error)?.message || err).includes('Failed to fetch')) {
        console.error('[PendingCounts] Errore:', err);
      }
      setCounts(prev => ({ ...prev, loading: false }));
    }
  }, [isAuthenticated, sedeCorrente]);

  // Carica all'avvio
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Refresh periodico (fallback se WebSocket non disponibile)
  useEffect(() => {
    const interval = setInterval(fetchCounts, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // Ascolta eventi WebSocket per aggiornamenti real-time
  useEffect(() => {
    const unregister = registerMessageHandler('pending_counts_update', (data) => {
      console.log('[PendingCounts] 📢 Ricevuto aggiornamento real-time:', data);

      // Notifica audio per aggiornamenti magazzino
      if (data.source === 'magazzino') {
        playMagazzinoNotification();
      }

      // Refresh immediato dei conteggi
      fetchCounts();
    });

    return () => {
      unregister();
    };
  }, [fetchCounts]);

  return (
    <PendingCountsContext.Provider value={{ ...counts, refresh: fetchCounts }}>
      {children}
    </PendingCountsContext.Provider>
  );
}

export function usePendingCounts() {
  const context = useContext(PendingCountsContext);
  if (!context) {
    // Fallback se usato fuori dal provider - ritorna valori di default
    return {
      apparati: 0,
      clienti: 0,
      analisi: 0,
      preventivi: 0,
      magazzino: 0,
      total: 0,
      loading: false,
      refresh: async () => {}
    };
  }
  return context;
}

export default PendingCountsContext;
